import React, { useState, FC, useCallback, useEffect } from "react";
import { Input, Button, Typography, Checkbox } from "antd";
import {
  LeftOutlined,
  PlusOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { AppButton } from "site/ui";
import { IQuestion, IQuestionAnswer, IQuestionApiInput, IQuestionOption, IQuiz, IQuizUpdateData } from "utils/types";
import { QuizAnswers } from "./QuizAnswers";
import { QuizOptions } from "./QuizOptions";
import { useNotification } from "providers";
import { useModalStore } from "store";

const { Title, Text } = Typography;
const { TextArea } = Input;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  padding: 20px;
  font-family: "Manrope", sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  width: 100%;
  font-family: "Manrope", sans-serif;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  color: #5575f7;
  font-size: 14px;
  padding: 0;
  border: none;
  box-shadow: none;
  font-family: "Manrope", sans-serif;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;

const FormTitle = styled(Title)`
  margin: 0 !important;
  font-size: 22px !important;
  font-weight: 600;
  font-family: "Manrope", sans-serif;
`;

const YearSection = styled.div`
  margin-bottom: 24px;
`;

const YearLabel = styled(Text)`
  font-size: 14px;
  color: #666;
  display: block;
  font-family: "Manrope", sans-serif;
  margin-bottom: 8px;
`;

const QuestionCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  background-color: #fff;
  font-family: "Manrope", sans-serif;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
`;

const QuestionTitle = styled(Text)`
  font-weight: 500;
  font-size: 16px;
`;

const QuestionContent = styled.div`
  margin-top: 16px;
`;

const OptionLabel = styled(Text)`
  font-size: 14px;
  color: #666;
  display: block;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const AddOptionButton = styled(Button)`
  color: #5575f7;
  padding-left: 0;
  margin-top: 8px;
  border: none;

  &:hover {
    background: transparent;
    color: #4563e6;
  }
`;

const ExplanationSection = styled.div`
  margin-top: 20px;
`;

const AddQuestionButton = styled.div`
  border: 1px solid #5575f7;
  border-radius: 24px;
  padding: 10px;
  display: block;
  color: #5575f7;
  padding: 10px;
  font-size: 12px;
  width: 160px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type QuestionID = string;
type OptionID = string;
type QuestionAnswerID = string;
export interface CreateQuizProps {
  isEdit?: boolean;
  quizId?: string;
  onBack: (newQuiz?: IQuiz) => void;
  onDeleteQuestionOption: (questionId: string, optionId: string) => Promise<boolean>;
  onGetQuiz: (quizId: string) => Promise<IQuiz|undefined>;
  onGetQuizQuestions: (quizId: string) => Promise<IQuestion[]>;
  onGetCorrectAnswers: (quizId: string) => Promise<IQuestionAnswer[]>;
  onUpdateQuiz: (data: IQuizUpdateData) => Promise<IQuiz|undefined>;
  onCreateQuiz: (data: {
    quiz: Partial<IQuiz>;
    questions: IQuestionApiInput[];
    answers: Partial<IQuestionAnswer>[]
  }) => Promise<IQuiz|undefined>
}
export const CreateQuiz:FC<CreateQuizProps> = ({
  onBack, onCreateQuiz, isEdit, quizId, onGetQuizQuestions,
  onGetQuiz, onGetCorrectAnswers, onDeleteQuestionOption,
  onUpdateQuiz
}) => {
  const notification = useNotification();
  const { openModal, closeModal } = useModalStore();
  const [processing, setProcessing] = useState(false);
  const [quiz, setQuiz] = useState<Partial<IQuiz>>({});
  const [questions, setQuestions] = useState<Partial<IQuestion>[]>([{id: "1"}]);


  const [options, setOptions] = useState<Record<string, IQuestionOption[]>>({
    [String(questions[0]?.id)]: [],
  });
  const [questionAnswers, setQuestionAnswers] = useState<IQuestionAnswer[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  // editing quiz starts
  const [editedQuestions, setEditedQuestions] = useState<IQuizUpdateData["questions"]>({});
  const [editedOptions, setEditedOptions] = useState<IQuizUpdateData["questionOptions"]>({});
  const [editedAnswers, setEditedAnswers] = useState<IQuizUpdateData["questionAnswers"]>({});
  const [editedQuiz, setEditedQuiz] = useState<Partial<IQuiz>>({});
  
  // editing quiz ends
  const handleUpdate = useCallback(async ( ) => {
    if(!quizId){
      notification.error({ message: "quiz id not found"})
      return;
    }
   const newQuiz = await onUpdateQuiz({
      quiz: {quizId, quiz: editedQuiz},
      questions: editedQuestions,
      questionOptions: editedOptions,
      questionAnswers: editedAnswers
    })
    if(Boolean(newQuiz)){
      onBack(newQuiz)
    }
  }, [
    JSON.stringify(editedQuiz), JSON.stringify(editedQuestions), 
    JSON.stringify(editedOptions), JSON.stringify(editedAnswers)
  ])
  const handleSubmit = useCallback(async () => {
    if(!quiz){
      return;
    }
    const questionData:IQuestionApiInput[] = questions.map((q) => {
      return {
        ...q,
        options: options[String(q.id)],
      }
    })
    setProcessing(true)
    const result = await onCreateQuiz({
      quiz, questions: questionData, answers: questionAnswers
    })
    if(result){
      notification.success({ message: "Past question created!" })
      onBack(result)
    }
    setProcessing(false)
  }, [
    JSON.stringify(questions), 
    JSON.stringify(questionAnswers), 
    JSON.stringify(options),
    JSON.stringify(quiz)
  ]);

  const deleteOptionOnEdit = useCallback(async (questionId:QuestionID, optionId:OptionID, questionAnswerId?: QuestionAnswerID) => {
    const result = await onDeleteQuestionOption(questionId, optionId)
    if(!result){
      return;
    }
    const newOptions = options[questionId].filter((opt) => opt.id !== optionId)
    options[questionId] = newOptions;
    setOptions(structuredClone(options));
    // check if this in the answers and remove it
    const questionAnsIndex = questionAnswers.findIndex((ans) => ans.questionId === questionId)
    const newAnsOption = questionAnswers[questionAnsIndex].optionIds.filter((opt) => opt !== optionId)
    questionAnswers[questionAnsIndex].optionIds = newAnsOption;
    setQuestionAnswers(structuredClone(questionAnswers));
    // check if this in the edited answers and remove it
    if(questionAnswerId && editedAnswers?.[questionId]?.[questionAnswerId]?.optionIds){
      const editedAnsOptionIdex = editedAnswers[questionId][questionAnswerId]?.optionIds?.findIndex((opt) => opt === optionId)
      if(editedAnsOptionIdex !== undefined && editedAnsOptionIdex !== -1){
        editedAnswers?.[questionId]?.[questionAnswerId]?.optionIds?.splice(editedAnsOptionIdex, 1)
        setEditedAnswers(structuredClone(editedAnswers))      
      }
    }
  }, [
    JSON.stringify(options), JSON.stringify(questionAnswers),
    JSON.stringify(editedOptions), JSON.stringify(editedAnswers)
  ]);
  const confirmDeleteOption = useCallback((questionId:QuestionID, optionId:OptionID, questionAnswerId?: QuestionAnswerID) => {
    openModal(
      "Are you sure you want to delete this option?",
      "This action cannot be undone",
      async () => {
        await deleteOptionOnEdit(questionId, optionId, questionAnswerId)
        closeModal();
      })
  }, [
    JSON.stringify(options), JSON.stringify(questionAnswers),
    JSON.stringify(editedOptions), JSON.stringify(editedAnswers)
  ]);
  const getDataForEdit = useCallback(async () => {
    if(!quizId || !isEdit) return
    const [quiz, questions, correctAnswers] = await Promise.all([
      onGetQuiz(quizId),
      onGetQuizQuestions(quizId),
      onGetCorrectAnswers(quizId)
    ])
    if(quiz) setQuiz(structuredClone(quiz))
    setQuestions(structuredClone(questions))
    setQuestionAnswers(structuredClone(correctAnswers))
    const options:Record<string, IQuestionOption[]> = {}
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if(question.options){
        options[question.id] = question.options
      }
    }
    setOptions(options)
  }, [quizId, isEdit])
  useEffect(() => {
    if(isEdit && quizId){
      getDataForEdit()
    }
  }, [quizId, isEdit])

  return (
    <Container>
      <Header>
        <BackButton type="text" icon={<LeftOutlined />} onClick={() => onBack()}>
          Course
        </BackButton>
        <AppButton variant="primary" loading={processing} onClick={isEdit ? handleUpdate : handleSubmit}>
          {isEdit ? "Update": "Publish"} past question
        </AppButton>
      </Header>

      <FormTitle level={3}>Past questions</FormTitle>

      <YearSection>
        <YearLabel>Title</YearLabel>
        <Input
          value={quiz?.year || ""}
          onChange={(e) => {
            setQuiz({...quiz, year: e.target.value})
            if(isEdit){
              setEditedQuiz({...editedQuiz, year: e.target.value})
            }
          }}
          size="large"
          style={{ width: 200 }}
        />
      </YearSection>

      {questions?.map((question, questionIndex) => {
        const questionId = String(question.id);
        const isExpanded = expandedIds.includes(String(questionId));
        // const questionIndex = questions.findIndex((q) => q.id === questionId)
        // const question = questions[questionIndex];

        const questionAnsIndex = questionAnswers?.findIndex(item => item.questionId === questionId);
        const questionAns = questionAnswers[questionAnsIndex]

        const questionOptionsArray = options[questionId] || [];
        return (
          <QuestionCard key={`${questionId}-${questionIndex}`}>
            <QuestionHeader onClick={() => {
              if (isExpanded) {
                setExpandedIds(expandedIds.filter(id => id !== String(questionId)))
              } else {
                setExpandedIds([...expandedIds, String(questionId)])
              }
            }}>
              <QuestionTitle>Question {questionIndex + 1}</QuestionTitle>
              {isExpanded  ? <UpOutlined /> : <DownOutlined />}
            </QuestionHeader>

            {isExpanded && (
              <QuestionContent>
                <Input.TextArea
                  value={question?.content}
                  onChange={(e) => {
                    if(questions[questionIndex]){
                      questions[questionIndex].content = e.target.value;
                    }else {
                      questions[questionIndex] = {
                        content: e.target.value
                      }
                    }
                    setQuestions([...questions]);
                    if(isEdit){
                      setEditedQuestions({
                        ...editedQuestions, 
                        [questionId]: {
                          ...editedQuestions[questionId],
                          content: e.target.value
                        }})
                    }
                  }}
                  placeholder="Enter question text"
                  rows={3}
                />

                <div style={{ textAlign: "right", marginTop: 8 }}>
                  <Checkbox
                    checked={question?.allowMultiple === "true"}
                    onChange={(e) => {
                      if(!question){
                        questions.push({
                          id: String(questions.length + 1),
                          allowMultiple: e.target.checked ? "true" : "false",
                        })
                      }else{
                        question.allowMultiple = e.target.checked ? "true" : "false";
                        questions[questionIndex] = question;
                      }
                      setQuestions([...questions]);
                      if(!questionAnswers[questionAnsIndex]){
                        questionAnswers.push({
                          questionId: questionId,
                          optionIds: [],
                          id: String(questionAnswers.length + 1),
                          quizId: ""
                        })
                      }else {
                        questionAnswers[questionAnsIndex].optionIds = []
                      }
                      setQuestionAnswers([...questionAnswers])
                      if(isEdit){
                        setEditedQuestions({
                          ...editedQuestions, 
                          [questionId]: {
                            ...editedQuestions[questionId], 
                            allowMultiple: e.target.checked ? "true" : "false"
                          }})
                      }
                    }}
                  >
                    Allow multiple answers
                  </Checkbox>
                </div>

                <QuizOptions {...{
                  isEdit, 
                  onUpdateQuestionOption: (optionId, questionOption) => {
                    setEditedOptions({
                      ...editedOptions,
                      [questionId]: {
                        ...editedOptions[questionId],
                        [optionId]: {
                          ...editedOptions[questionId]?.[optionId],
                          ...questionOption,
                        }
                      }
                    })
                  },
                  onDeleteOption: (optionId) => {
                    confirmDeleteOption(questionId, optionId, questionAns?.id)
                  },
                  questionOptions: structuredClone(options[questionId] || []),
                  setQuestionOptions(questionOptions, questionOptionId) {
                    setOptions({
                      ...options, [questionId]: questionOptions
                    })
                    if(questionOptionId !== undefined){
                      if(questionAns?.optionIds?.includes(questionOptionId)){
                        const questionAnsOptionIndex = questionAns.optionIds.indexOf(questionOptionId)
                        questionAns.optionIds.splice(questionAnsOptionIndex, 1)
                        questionAnswers[questionAnsIndex] = {...questionAns};
                        setQuestionAnswers([...questionAnswers])
                      }
                    }
                  },
                }}/>

                <AddOptionButton
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setOptions({
                      ...options, 
                      [questionId]: [
                        ...questionOptionsArray,
                        {
                          id: String(questionOptionsArray.length + 1),
                          text: "",
                          questionId: "",
                        },
                      ]
                    })
                  }}
                >
                  Add option
                </AddOptionButton>

                <QuizAnswers {...{
                  options: options[questionId],
                  answerOptionIds: questionAns?.optionIds || [],
                  allowMultiple: question?.allowMultiple,
                  updateAnswerOptionIds(optionIds) {
                     if(!questionAns){
                      questionAnswers.push({
                        id: String(questionAnswers.length + 1),
                        questionId, optionIds,
                        quizId: ""
                      })
                    }else {
                      questionAns.optionIds = optionIds
                      questionAnswers[questionAnsIndex] = questionAns;
                    }
                    setQuestionAnswers([...questionAnswers])

                    if(isEdit){
                      setEditedAnswers({
                        ...editedAnswers,
                        [questionId]: {
                          ...editedAnswers[questionId],
                          [questionAns?.id]: {
                            ...editedAnswers[questionId]?.[questionAns?.id],
                            optionIds
                          }
                        }
                      })
                    }
                  },
                }}/>

                <ExplanationSection>
                  <OptionLabel>Explanation (optional)</OptionLabel>
                  <TextArea
                    value={questionAns?.explanation}
                    onChange={(e) => {
                      if(!questionAns){
                        questionAnswers.push({
                          id: String(questionAnswers.length + 1),
                          questionId: String(questionId), optionIds: [],
                          explanation: e.target.value,
                          quizId: ""
                        })
                      }else {
                        questionAns.explanation = e.target.value
                        questionAnswers[questionAnsIndex] = questionAns;
                        // if(isEdit){
                        //   editedAnswers[questionId] = {
                        //     ...editedAnswers[questionId],
                        //     [questionAns?.id]: {
                        //       ...editedAnswers[questionId]?.[questionAns?.id],
                        //       explanation: e.target.value
                        //     }
                        //   }
                        // }
                      }
                      setQuestionAnswers([...questionAnswers])
                      if(isEdit){
                        setEditedAnswers({
                          ...editedAnswers,
                          [questionId]: {
                            ...editedAnswers[questionId],
                            [String(questionAns?.id || questionAnswers.length)]: {
                              ...editedAnswers[questionId]?.[questionAns?.id],
                              explanation: e.target.value
                            }
                          }
                        })
                      }
                    }}
                    placeholder="Enter explanation"
                    rows={3}
                  />
                </ExplanationSection>
              </QuestionContent>
            )}
          </QuestionCard>
        )
      })}

      <AddQuestionButton onClick={() => {
        setQuestions([...questions, {
          id: String(questions.length + 1),
          content: "",
          allowMultiple: "false",
          options: [],
        }])
      }}>
        Add another question
      </AddQuestionButton>
    </Container>
  );
};
